"use client";

import { useMemo, useState } from "react";
import {
  Button,
  FormField,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  useToast,
} from "@chotca/ui";
import { useCreateSlot, useSlotsForLocation } from "@chotca/api-client";
import type { InterviewSlot } from "@chotca/sdk";
import {
  INTERVIEW_DAY_END_HOUR,
  INTERVIEW_DAY_END_TIME,
  INTERVIEW_DAY_START_HOUR,
  INTERVIEW_DAY_START_TIME,
  SLOT_STEP_MINUTES,
  SLOT_STEP_SECONDS,
} from "@chotca/config";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";

const WEEKDAY_LABELS = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"] as const;

/**
 * Valid interview start times: every {@link SLOT_STEP_MINUTES} from
 * {@link INTERVIEW_DAY_START_HOUR} to {@link INTERVIEW_DAY_END_HOUR}, inclusive.
 * Offering only these as a dropdown enforces both the step and the window on
 * every platform — unlike `<input type="time">` min/max/step, which iOS's
 * native wheel silently ignores.
 */
const TIME_OPTIONS: { value: string; label: string }[] = (() => {
  const opts: { value: string; label: string }[] = [];
  for (
    let m = INTERVIEW_DAY_START_HOUR * 60;
    m <= INTERVIEW_DAY_END_HOUR * 60;
    m += SLOT_STEP_MINUTES
  ) {
    const hhmm = `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(
      m % 60,
    ).padStart(2, "0")}`;
    opts.push({ value: hhmm, label: hhmm });
  }
  return opts;
})();

/**
 * Weekly interview-slot calendar for one location. Shared by:
 *   - {@link InviteApplicantDialog} in `pick` mode (employer ticks options to offer)
 *   - the "Tìm ứng viên" talent deck in `pick` mode (sourced-invite slots)
 *
 * Owns its own week navigation, add-slot popover, and slot fetch/create. Picks
 * are controlled by the parent so callers can drive a submit button / count.
 */
export function SlotCalendar({
  orgId,
  locationId,
  mode = "pick",
  picked,
  onTogglePick,
  maxPick = 5,
  onCreated,
}: {
  orgId: string;
  locationId: string;
  /** `pick`: slots are selectable options. `manage`: read-only pills, add only. */
  mode?: "pick" | "manage";
  picked?: Set<string>;
  onTogglePick?: (id: string) => void;
  maxPick?: number;
  /** Fired after a slot is created so callers can auto-select it in pick mode. */
  onCreated?: (slot: InterviewSlot) => void;
}) {
  const { success: toastSuccess, error: toastError } = useToast();
  const slotsQ = useSlotsForLocation(orgId, locationId);
  const slots = useMemo(() => slotsQ.data?.items ?? [], [slotsQ.data]);
  const createSlot = useCreateSlot(orgId);

  const [weekStart, setWeekStart] = useState<Date>(() =>
    startOfWeek(new Date()),
  );
  const [creatingFor, setCreatingFor] = useState<string | null>(null);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );

  const slotsByDay = useMemo(() => {
    const now = Date.now();
    const map = new Map<string, InterviewSlot[]>();
    for (const s of slots) {
      const start = new Date(s.startAt);
      if (start.getTime() < now) continue; // hide past slots
      const key = dayKey(start);
      const arr = map.get(key);
      if (arr) arr.push(s);
      else map.set(key, [s]);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => a.startAt.localeCompare(b.startAt));
    }
    return map;
  }, [slots]);

  async function handleCreateSlot(
    dayISO: string,
    time: string,
    capacity: number,
  ) {
    if (!locationId || !time) return;
    const [hhStr, mmStr] = time.split(":");
    const hh = Number(hhStr);
    const mm = Number(mmStr);
    if (mm % (SLOT_STEP_SECONDS / 60) !== 0) {
      toastError(`Giờ phải theo bước ${SLOT_STEP_SECONDS / 60} phút`);
      return;
    }
    const minutesOfDay = hh * 60 + mm;
    if (
      minutesOfDay < INTERVIEW_DAY_START_HOUR * 60 ||
      minutesOfDay > INTERVIEW_DAY_END_HOUR * 60
    ) {
      toastError(
        `Chỉ chọn giờ trong khoảng ${INTERVIEW_DAY_START_TIME}–${INTERVIEW_DAY_END_TIME}`,
      );
      return;
    }
    const [yyStr, moStr, ddStr] = dayISO.split("-");
    const startAt = new Date(
      Number(yyStr),
      Number(moStr) - 1,
      Number(ddStr),
      Number(hhStr),
      Number(mmStr),
      0,
      0,
    );
    if (startAt.getTime() < Date.now()) {
      toastError("Khung giờ đã qua");
      return;
    }
    try {
      const created = await createSlot.mutateAsync({
        orgAddressId: locationId,
        startAt: startAt.toISOString(),
        capacityTotal: capacity,
      });
      setCreatingFor(null);
      onCreated?.(created);
      toastSuccess("Đã tạo khung giờ");
    } catch (e) {
      toastError(e instanceof Error ? e.message : "Tạo khung giờ thất bại");
    }
  }

  const todayKey = dayKey(new Date());
  const canGoPrev = weekStart.getTime() > startOfWeek(new Date()).getTime();

  return (
    <div className="flex flex-col gap-3">
      {/* Week navigation */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setWeekStart((d) => addDays(d, -7))}
          disabled={!canGoPrev}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-smoke text-ink transition-colors hover:bg-mist disabled:opacity-40"
          aria-label="Tuần trước"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="text-sm font-semibold text-ink">
          Tuần {fmtDayMonth(days[0]!)} – {fmtDayMonth(days[6]!)}
        </div>
        <button
          type="button"
          onClick={() => setWeekStart((d) => addDays(d, 7))}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-smoke text-ink transition-colors hover:bg-mist"
          aria-label="Tuần sau"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Calendar grid — all 7 columns always fit, no horizontal scroll */}
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
        {days.map((day, idx) => {
          const key = dayKey(day);
          const daySlots = slotsByDay.get(key) ?? [];
          const isToday = key === todayKey;
          const isPast = day.getTime() < new Date().setHours(0, 0, 0, 0);
          return (
            <div key={key} className="flex min-w-0 flex-col gap-1.5">
              {/* Day header: date stacked over weekday */}
              <div
                className={`flex flex-col items-center rounded-lg px-0.5 py-1 ${
                  isToday ? "bg-lime/15" : ""
                }`}
              >
                <span className="text-sm font-bold leading-none text-ink tabular-nums">
                  {day.getDate()}
                </span>
                <span className="mt-0.5 text-[11px] font-semibold text-ash">
                  {WEEKDAY_LABELS[idx]}
                </span>
              </div>

              <div className="flex flex-col gap-1">
                {daySlots.map((s) => {
                  const full = s.capacityRemaining <= 0;
                  const isPicked = mode === "pick" && !!picked?.has(s.id);
                  const reachedMax =
                    mode === "pick" &&
                    !isPicked &&
                    (picked?.size ?? 0) >= maxPick;
                  const interactive = mode === "pick" && !full && !reachedMax;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      disabled={mode === "pick" ? full || reachedMax : true}
                      onClick={
                        interactive ? () => onTogglePick?.(s.id) : undefined
                      }
                      className={`flex min-w-0 flex-col items-center rounded-md border px-0.5 py-1 text-center transition-colors ${
                        isPicked
                          ? "border-lime bg-lime/20"
                          : full
                            ? "cursor-not-allowed border-smoke bg-mist/40 opacity-50"
                            : reachedMax
                              ? "cursor-not-allowed border-smoke bg-paper opacity-40"
                              : mode === "manage"
                                ? "cursor-default border-smoke bg-paper"
                                : "border-smoke bg-paper hover:border-ink"
                      }`}
                      title={
                        full
                          ? "Khung giờ đã đầy"
                          : reachedMax
                            ? `Tối đa ${maxPick} khung giờ`
                            : ""
                      }
                    >
                      <span className="text-xs font-bold text-ink tabular-nums">
                        {fmtTime(s.startAt)}
                      </span>
                      <span className="text-[10px] leading-tight text-ash">
                        {/* Show "booked / total" (e.g. 0/3 right after creating),
                            not "remaining / total" — a brand-new empty slot
                            reading 3/3 confused employers. */}
                        {full
                          ? "Đã đầy"
                          : `${s.capacityTotal - s.capacityRemaining}/${s.capacityTotal}`}
                      </span>
                    </button>
                  );
                })}

                {/* Always render the add button — past days show a disabled
                    variant rather than nothing, so the row stays consistent. */}
                <AddSlotButton
                  open={creatingFor === key}
                  anchorOnRight={idx >= 4}
                  disabled={isPast}
                  onOpenChange={(next) => setCreatingFor(next ? key : null)}
                  onSubmit={(time, cap) => handleCreateSlot(key, time, cap)}
                  pending={createSlot.isPending}
                />
              </div>
            </div>
          );
        })}
      </div>

      {slots.length === 0 && !slotsQ.isLoading ? (
        <p className="text-center text-sm text-ash">
          Chưa có khung giờ nào. Nhấn dấu <strong>+</strong> trên ngày bạn có
          thể phỏng vấn để thêm.
        </p>
      ) : null}
    </div>
  );
}

export function AddSlotButton({
  open,
  anchorOnRight,
  disabled,
  onOpenChange,
  onSubmit,
  pending,
}: {
  open: boolean;
  anchorOnRight: boolean;
  disabled?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (time: string, capacity: number) => void;
  pending: boolean;
}) {
  // Past days: keep the slot visible but clearly disabled, so every column has
  // the same affordance instead of some columns silently missing the button.
  if (disabled) {
    return (
      <button
        type="button"
        disabled
        aria-label="Ngày đã qua — không thể thêm khung giờ"
        title="Ngày đã qua"
        className="flex w-full cursor-not-allowed items-center justify-center rounded-md border border-dashed border-smoke py-1.5 text-ash opacity-40"
      >
        <Plus size={14} />
      </button>
    );
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Thêm khung giờ"
          title="Thêm khung giờ"
          className={`flex w-full items-center justify-center rounded-md border border-dashed py-1.5 transition-colors ${
            open
              ? "border-lime bg-lime/10 text-ink"
              : "border-smoke text-ash hover:border-ink hover:text-ink"
          }`}
        >
          <Plus size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={anchorOnRight ? "end" : "start"}
        // Don't pull focus into the time input on open — on iOS that shifts the
        // viewport before the panel has settled. Let the employer tap the field
        // themselves; the native picker opens on that tap.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <CreateSlotForm
          onSubmit={onSubmit}
          onCancel={() => onOpenChange(false)}
          pending={pending}
        />
      </PopoverContent>
    </Popover>
  );
}

export function CreateSlotForm({
  onSubmit,
  onCancel,
  pending,
}: {
  onSubmit: (time: string, capacity: number) => void;
  onCancel: () => void;
  pending: boolean;
}) {
  const [time, setTime] = useState("09:00");
  const [capacity, setCapacity] = useState(1);

  return (
    <>
      <p className="mb-2 text-xs font-semibold text-ink">Tạo khung giờ</p>

      <FormField
        label={`Giờ bắt đầu (${INTERVIEW_DAY_START_TIME}–${INTERVIEW_DAY_END_TIME}, mỗi ${SLOT_STEP_MINUTES} phút)`}
      >
        {/* A constrained dropdown rather than <input type="time">: only valid
            step-aligned times within the window are selectable, on every
            platform. */}
        <Select
          value={time}
          onValueChange={setTime}
          options={TIME_OPTIONS}
          placeholder="Chọn giờ"
        />
      </FormField>

      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs font-medium text-ash">Số chỗ phỏng vấn</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setCapacity((c) => Math.max(1, c - 1))}
            disabled={capacity <= 1}
            className="flex h-7 w-7 items-center justify-center rounded-full border border-smoke text-sm disabled:opacity-40"
            aria-label="Giảm"
          >
            −
          </button>
          <span className="w-5 text-center text-sm font-bold">{capacity}</span>
          <button
            type="button"
            onClick={() => setCapacity((c) => Math.min(5, c + 1))}
            disabled={capacity >= 5}
            className="flex h-7 w-7 items-center justify-center rounded-full border border-smoke text-sm disabled:opacity-40"
            aria-label="Tăng"
          >
            +
          </button>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Button variant="ghost" onClick={onCancel} className="flex-1" size="sm">
          Hủy
        </Button>
        <Button
          onClick={() => onSubmit(time, capacity)}
          disabled={pending || !time}
          className="flex-1"
          size="sm"
        >
          Tạo
        </Button>
      </div>
    </>
  );
}

// ----------------------------------------------------------------------------
// Date helpers
// ----------------------------------------------------------------------------
export function startOfWeek(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  // Monday = 1, Sunday = 0; shift so Monday is start of week.
  const dow = out.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  out.setDate(out.getDate() + diff);
  return out;
}

export function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

export function dayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function fmtDayMonth(d: Date): string {
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
